import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import Layout from "./layout/Layout";
import ConsentPage from "./pages/ConsentPage";
import NormeGraphique from "./pages/NormeGraphique";
import { useTranslation } from "react-i18next";
import i18n from "./i18n";
import DashboardPage from "./pages/DashboardPage";
import { useEffect, useState } from "react";
import { useSessionContext } from "./contexts/SessionContext";
import ConsultPage from "./pages/Consult/ConsultPage";
import ProfilePage from "./pages/Profile/ProfilePage";
import CreateConsultPage from "./pages/CreateConsult/CreateConsultPage";
import NetworkError from "./pages/NetworkError";
import { AxiosError } from "axios";
import LoadingFullScreen from "./components/LoadingFullScreen";
import Page404 from "./pages/Page404";
import MeHelpers from "./services/Authentification/MeHelpers";
import OrgAdminStatsPage from "./pages/OrgAdminStatsPage";
import ArchivesPage from "./pages/Archives/ArchivesPage";
import ArchiveConsultPage from "./pages/Archives/ArchiveConsultPage";
import TagManager from "react-gtm-module";
import "./App.scss";

function App() {
  const { t } = useTranslation();
  const { initialize, initStage, initialized: sessionInitialized, consentGiven, organization, user } = useSessionContext();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [error, setError] = useState<string | undefined>(undefined);
  const [appInitialized, setAppInitialized] = useState(false);

  useEffect(() => {
    const getOrgIdFromLocation = async () => {
      const route = "/organizations/";

      let orgId = "dermago";
      if (pathname.startsWith(route)) {
        let path = pathname.substring(route.length);
        const n = path.indexOf("/");
        if (n > 1) path = path.substring(0, n);
        orgId = path;
        //console.log("path", path);
      }

      return orgId;
    };

    const init = async () => {
      console.log("app.init(INITIALIZED)");

      try {
        const orgId = await getOrgIdFromLocation();
        const hasAuthResponse =
          window.location.search.includes("code=") ||
          window.location.search.includes("error=") ||
          window.location.hash.includes("code=") ||
          window.location.hash.includes("error=") ||
          window.location.hash.includes("id_token=") ||
          window.location.hash.includes("access_token=");

        //Hack since the app.dermago.ca redirect to /organizations/dermsmart and not to dermago
        console.log("app.init : referrer", `"${document.referrer}"`, "orgId", orgId, "host", window.location.host);
        if (hasAuthResponse) {
          console.log("app.init : auth response detected, skipping org redirect");
        } else if (document.referrer.startsWith("http://app.dermago.ca") || document.referrer.startsWith("https://app.dermago.ca")) {
          console.log(`Redirect from referrer '${document.referrer}' to 'https://app.dermsmart.ca/organizations/dermago'`);
          document.location.href = "https://app.dermsmart.ca/organizations/dermago";
        } else if (window.location.host === "dermsmart.ca") {
          console.log(`Redirect from referrer '${document.referrer}' to 'https://app.dermsmart.ca/organizations/dermago'`);
          document.location.href = "https://app.dermsmart.ca/organizations/dermago";
        } else if (window.location.host === "app.dermago.ca") {
          console.log("need redirect to dermago organization");
          document.location.href = "https://app.dermsmart.ca/organizations/dermago";
        } else if (window.location.host === "app.tontest.ca") {
          console.log("need redirect to tontest organization");
          document.location.href = "https://app.dermsmart.ca/organizations/tontest";
        } else if (window.location.pathname === "/") {
          console.log(`need redirect to '${orgId}' organization`);
          document.location.href = `${window.location.href}organizations/${orgId}`;
        }

        await initialize(orgId);
      } catch (e) {
        const axiosErr = e as AxiosError;
        if (axiosErr) setError(axiosErr.message);
        else setError(`Unknown error. ${e}`);

        console.error("App.init() -> unexpected error", e);
        // setReady(true);
      }
    };

    if (!appInitialized) {
      setAppInitialized(true);
      console.log(`App.useEffect: DERMSMART_API=${process.env.REACT_APP_DERMSMART_API}`);
      init();
    }
  }, [appInitialized, initialize, pathname]);

  useEffect(() => {
    if (organization?.organization.embedded) {
      const baseUrl = `/organizations/${organization?.organization.id}`;
      const baseUrl2 = `/organizations/${organization?.organization.id}/`;
      if (window.location.pathname === baseUrl || window.location.pathname === baseUrl2) {
        let url = `/organizations/${organization?.organization.id}/new-consult`;
        console.log("Embedded - Redirecting to", url, window.location.href);
        navigate(url);
      } else {
        console.log("Embedded - No Redirect", window.location.pathname);
      }
    }
  }, [organization, sessionInitialized, navigate]);

  useEffect(() => {
    try {
      if (organization?.organization?.analytics?.gtm) {
        console.log("Initialized GTM", organization.organization.analytics.gtm);
        TagManager.initialize({ gtmId: organization.organization.analytics.gtm });
      }
    } catch (e) {
      console.error(`app.init(OrgChange, ${organization?.organization?.id}) -> unexpected error`, e);
    }
  }, [organization]);

  useEffect(() => {
    document.title = t("app.title");
  }, [i18n.language]);

  const isOrgAdmin = MeHelpers.isOrgAdmin(user);

  return (
    <>
      <Layout>
        {error ? (
          <NetworkError error={error} />
        ) : (
          <>
            {!appInitialized && <div className="notif-bottom-overlay">{`${t("app.initializing")} ${initStage}`}</div>}
            {(!sessionInitialized || !appInitialized) && !organization?.organization?.authentificationless && <LoadingFullScreen center={true} />}

            <Routes>
              {error !== undefined ? (
                <Route path="*" element={<NetworkError error={error} />}></Route>
              ) : (
                <>
                  <Route path="/organizations/:organizationId" element={<DashboardPage />}></Route>
                  <Route path="/" element={<DashboardPage />}></Route>

                  {consentGiven ? (
                    <>
                      <Route path="/organizations/:organizationId/new-consult" element={<CreateConsultPage />}></Route>
                      <Route path="/organizations/:organizationId/identify-condition" element={<CreateConsultPage step={3} />}></Route>
                    </>
                  ) : (
                    <Route path="/organizations/:organizationId/new-consult" element={<ConsentPage />}></Route>
                  )}

                  <Route path="/organizations/:organizationId/consults/:consultId" element={<ConsultPage />}></Route>

                  <Route path="/profile" element={<ProfilePage />}></Route>

                  {isOrgAdmin && <Route path="/admin/stats" element={<OrgAdminStatsPage />}></Route>}

                  {isOrgAdmin && (organization?.organization.id === "dermago" || organization?.organization.id === "tontest") && (
                    <>
                      <Route path="/organizations/:organizationId/admin/archives" element={<ArchivesPage />}></Route>
                      <Route path="/organizations/:organizationId/admin/archives/:consultId" element={<ArchiveConsultPage />}></Route>
                    </>
                  )}

                  <Route path="/norme-graphique" element={<NormeGraphique />}></Route>

                  <Route path="*" element={<Page404 />}></Route>
                </>
              )}
            </Routes>
          </>
        )}
      </Layout>
    </>
  );
}

export default App;
function universalLanguageDetect(arg0: {
  supportedLanguages: string[]; // Whitelist of supported languages, will be used to filter out languages that aren't supported
  fallbackLanguage: string; // Fallback language in case the user's language cannot be resolved
  errorHandler: (error: any) => void;
}) {
  throw new Error("Function not implemented.");
}
